import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { fetchJSON } from 'api/fetch';

import { SLICE_NAME } from './constants';
import {
  RoleLaborTotals,
  RoleLaborTotalsPayload,
  RoleLaborTotalsPayloadItem,
  RoleLaborTotalsState,
} from './types';

export const fetchRoleLaborTotals = createAsyncThunk<RoleLaborTotalsPayload>(
  `${SLICE_NAME}/fetch`,
  async () => {
    const resp = await fetchJSON(`/schedule_builder/labor`);
    return resp;
  }
);

const roleLaborTotalsAdapter = createEntityAdapter<RoleLaborTotals>();

export const initialState: RoleLaborTotalsState =
  roleLaborTotalsAdapter.getInitialState({
    status: 'idle',
    error: undefined,
  });

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchRoleLaborTotals.pending, state => {
      state.status = 'pending';
    });
    builder.addCase(fetchRoleLaborTotals.fulfilled, (state, action) => {
      const items = action.payload.data
        .filter(
          (item: RoleLaborTotalsPayloadItem) => item.type === 'roleLaborTotals'
        )
        .map((item: RoleLaborTotalsPayloadItem) => ({
          id: item.id,
          type: item.type,
          hours: item.attributes.hours,
          hasOvertime: item.attributes.hasOvertime,
          hourTotals: item.attributes.hourTotals,
          wages: item.attributes.wages,
          date: item.attributes.date,
          roleId: item.relationships.role.data?.id,
          jobId: item.relationships.job.data.id,
        }));
      roleLaborTotalsAdapter.setAll(state, items);
      state.status = 'succeeded';
    });
    builder.addCase(fetchRoleLaborTotals.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to fetch role labor totals';
    });
  },
});

export const reducer = slice.reducer;

export const {
  selectById: selectRoleLaborTotalsById,
  selectIds: selectRoleLaborTotalsIds,
  selectEntities: selectRoleLaborTotalsEntities,
  selectAll: selectAllRoleLaborTotals,
  selectTotal: selectTotalRoleLaborTotals,
} = roleLaborTotalsAdapter.getSelectors();
